/**
 * DSS Results — single output surface, compact chrome + tabbed report.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
  Stack,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { useAppSelector } from '../hooks/reduxHooks';
import ProfessionalNutrientReport from '../components/reports/ProfessionalNutrientReport';
import SimplePDFReport from '../components/reports/SimplePDFReport';
import DssPipelineStrip from '../components/workflow/DssPipelineStrip';
import DssContextStrip from '../components/workflow/DssContextStrip';
import { WorkflowStage } from '../store/slices/workflowSlice';
import { buildWorkflowResultsFromStage4 } from '../utils/buildWorkflowResultsFromStage4';

function buildReportFromWorkflow(
  calcResults: any,
  formulation: any,
  waterProfile: any,
  stockSolution: any
) {
  const salts =
    calcResults?.recipe?.salts ||
    calcResults?.recipe?.saltAmounts ||
    calcResults?.saltAmounts ||
    [];

  return {
    metadata: {
      recipeName: formulation?.name || 'Custom Recipe',
      cropType: formulation?.cropType || 'General',
      growthStage: 'General',
      targetEC: calcResults?.predictions?.finalEc || 2.0,
      targetPH: calcResults?.predictions?.finalPh || 6.5,
      waterSource: waterProfile?.name || 'Unknown',
      createdDate: new Date().toISOString(),
      stockVolumePerTank: stockSolution?.volume || 10,
      workingVolumeTarget:
        stockSolution?.workingSolutionVolume ||
        (stockSolution?.volume || 10) * (stockSolution?.dilutionRatio || 100),
      dilutionRatio: stockSolution?.dilutionRatio || 100,
      targetVolume: stockSolution?.volume || 10,
      concentrationUnit: 'mg/L',
      massUnit: 'g',
      modelVersion: 'FlahaFast DSS',
      calculationEngine: 'HydroBuddy matrix',
    },
    formulation: {
      name: formulation?.name || 'Custom',
      elements: formulation?.elements || [],
    },
    waterProfile: {
      name: waterProfile?.name || 'Unknown',
      elements: waterProfile?.elements || [],
    },
    saltAmounts: salts.map((salt: any, index: number) => ({
      id: salt.id || `salt_${index}`,
      name: salt.name || 'Unknown',
      formula: salt.formula || '',
      amount: Number(salt.amount) || 0,
      unit: salt.unit || 'g',
      elements: Array.isArray(salt.elements)
        ? salt.elements.map((el: any) => ({
            symbol: el.symbol || el.element?.symbol || '?',
            percentage: Number(el.percentage) || 0,
          }))
        : [],
      tankAssignment: salt.tankAssignment || 'A',
    })),
    achievedConcentrations:
      calcResults?.predictions?.achievedConcentrations || [],
    solutionProperties: {
      electricalConductivity: calcResults?.predictions?.finalEc || 0,
      finalPH: calcResults?.predictions?.finalPh || 6.5,
      totalDissolvedSolids:
        calcResults?.predictions?.totalDissolvedSolids || 0,
    },
    recipe: calcResults?.recipe,
    predictions: calcResults?.predictions,
    instructions: calcResults?.instructions,
  };
}

const Results: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const workflowCalc = useAppSelector((s) => s.workflow.calculationResults);
  const waterProfile = useAppSelector((s) => s.workflow.waterProfile);
  const formulation = useAppSelector((s) => s.workflow.formulation);
  const stockSolution = useAppSelector((s) => s.workflow.stockSolution);
  const navigationData = (location.state as any)?.calculationData;

  useEffect(() => {
    setLoading(true);
    setLoadError(null);
    try {
      if (navigationData) {
        setReportData(navigationData);
        setLoading(false);
        return;
      }
      if (workflowCalc) {
        setReportData(
          buildReportFromWorkflow(
            workflowCalc,
            formulation,
            waterProfile,
            stockSolution
          )
        );
        setLoading(false);
        return;
      }
      const stage4 = (stockSolution as any)?.calculationResult;
      if (stage4?.saltAmounts?.length) {
        const { reportData: fromStage4 } = buildWorkflowResultsFromStage4({
          stockCalculationResult: stage4,
          formulation,
          waterProfile,
          stockVolume: Number(stockSolution?.volume) || 10,
          dilutionRatio: Number(stockSolution?.dilutionRatio) || 100,
          workingSolutionVolume: (stockSolution as any)?.workingSolutionVolume,
        });
        setReportData(fromStage4);
        setLoading(false);
        return;
      }
      setReportData(null);
      setLoadError('No finalized recipe yet.');
      setLoading(false);
    } catch (err) {
      setReportData(null);
      setLoadError(err instanceof Error ? err.message : 'Failed to load results');
      setLoading(false);
    }
  }, [navigationData, workflowCalc, formulation, waterProfile, stockSolution]);

  const hasSalts = useMemo(
    () => (reportData?.saltAmounts?.length || 0) > 0,
    [reportData]
  );

  const handleDownload = async () => {
    if (!reportData?.saltAmounts?.length) {
      setSnackbarMessage('No salt data for PDF');
      setSnackbarOpen(true);
      return;
    }
    try {
      setPdfLoading(true);
      const pdfData = {
        ...reportData,
        metadata: {
          ...reportData.metadata,
          recipeName: reportData.metadata?.recipeName || 'FlahaFAST Recipe',
          createdDate:
            reportData.metadata?.createdDate || new Date().toISOString(),
          stockVolumePerTank: reportData.metadata?.stockVolumePerTank || 10,
          dilutionRatio: reportData.metadata?.dilutionRatio || 100,
        },
        saltAmounts: reportData.saltAmounts.map((salt: any) => ({
          ...salt,
          amount: salt.amount || 0,
          name: salt.name || 'Unknown',
          tankAssignment: salt.tankAssignment || 'A',
        })),
      };
      const blob = await pdf(<SimplePDFReport data={pdfData} />).toBlob();
      if (!blob?.size) throw new Error('Empty PDF');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      const name = (reportData.metadata?.recipeName || 'Recipe').replace(
        /[^a-zA-Z0-9]/g,
        '_'
      );
      saveAs(blob, `FlahaFAST_${name}_${ts}.pdf`);
      setSnackbarMessage('PDF saved');
      setSnackbarOpen(true);
    } catch (e) {
      setSnackbarMessage(
        `PDF failed: ${e instanceof Error ? e.message : 'error'}`
      );
      setSnackbarOpen(true);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" minHeight="30vh">
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Loading results…
          </Typography>
        </Stack>
      </Container>
    );
  }

  if (!reportData || loadError) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <DssPipelineStrip currentStage={WorkflowStage.UNIFIED_CALCULATOR} />
        <Alert severity="info">
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            No recipe results yet
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {loadError || 'Finalize Recipe (Verification) to open results.'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/stock-solutions')}
            >
              Open Recipe
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate('/dashboard')}
            >
              Workspace
            </Button>
          </Stack>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <DssPipelineStrip currentStage={WorkflowStage.UNIFIED_CALCULATOR} />
      <DssContextStrip
        stockVolumeL={stockSolution?.volume}
        volumeUnit={stockSolution?.volumeUnit}
        concentrationFactor={stockSolution?.dilutionRatio}
      />

      {/* Compact action bar — not a second report header */}
      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1.25,
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} lineHeight={1.2}>
            Results
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {reportData.metadata?.recipeName ||
              reportData.formulation?.name ||
              'Recipe report'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/stock-solutions')}
            variant="outlined"
          >
            Recipe
          </Button>
          <Button
            size="small"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
            variant="outlined"
          >
            Print
          </Button>
          <Button
            size="small"
            startIcon={
              pdfLoading ? <CircularProgress size={14} /> : <DownloadIcon />
            }
            onClick={handleDownload}
            variant="contained"
            disabled={pdfLoading || !hasSalts}
          >
            PDF
          </Button>
        </Stack>
      </Paper>

      {!hasSalts ? (
        <Alert severity="error">
          Incomplete salt amounts. Return to Recipe and re-run the solve.
        </Alert>
      ) : (
        <ProfessionalNutrientReport data={reportData} embedded />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default Results;
